import React, { useCallback, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, X, CheckCircle, AlertCircle, RefreshCw, Settings } from 'lucide-react';
import { generateStudyContent } from '../../lib/gemini';
import { useAuth } from '../../contexts/AuthContext';
import { extractTextFromDocument, ProgressCallback, DEFAULT_PROCESSING_CONFIG, DocumentProcessingConfig } from '../../utils/documentProcessor';

interface FileUploadProps {
  onUploadComplete: (content: any) => void;
}

interface UploadedFile {
  file: File;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  error?: string;
  extractedText?: string;
  studyContent?: any;
  progressMessage?: string;
}

// Utility functions for file validation
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const validateFileType = (file: File): boolean => {
  const allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
    'application/vnd.ms-powerpoint', // PPT
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // DOCX
  ];
  const allowedExtensions = ['pdf', 'pptx', 'ppt', 'docx'];
  const fileExtension = file.name.toLowerCase().split('.').pop();
  
  return allowedTypes.includes(file.type) || (fileExtension ? allowedExtensions.includes(fileExtension) : false);
};

const validateFileSize = (file: File, maxSizeMB: number): boolean => {
  return file.size <= maxSizeMB * 1024 * 1024;
};

const isValidDocument = (file: File): boolean => {
  return validateFileType(file) && file.size > 0;
};

export const FileUpload: React.FC<FileUploadProps> = ({ onUploadComplete }) => {
  const { userProfile, updateUserProfile } = useAuth();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [showConfig, setShowConfig] = useState(false);
  const [processingConfig, setProcessingConfig] = useState<DocumentProcessingConfig>(DEFAULT_PROCESSING_CONFIG);

  const processFile = async (file: File) => {
    try {
      console.log('Starting file processing:', file.name);

      // Update status to processing
      setUploadedFiles(prev => prev.map(f => 
        f.file.name === file.name 
          ? { ...f, status: 'processing', progress: 0, progressMessage: 'Starting file processing...' }
          : f
      ));

      // Additional validation
      if (!isValidDocument(file)) {
        throw new Error('Invalid document file. Please ensure the file is a valid PDF, PPTX, or DOCX document.');
      }

      if (file.size > 50 * 1024 * 1024) {
        throw new Error('File is too large. Maximum size is 50MB.');
      }

      // Create progress callback for real-time updates
      const progressCallback: ProgressCallback = (progress: number, message: string) => {
        setUploadedFiles(prev => prev.map(f => 
          f.file.name === file.name 
            ? { ...f, progress, progressMessage: message }
            : f
        ));
      };

      console.log('Extracting text from document...');
      const extractedText = await extractTextFromDocument(file, progressCallback, processingConfig);
      
      if (!extractedText || extractedText.trim().length < 10) {
        throw new Error('Could not extract sufficient text from the PDF. The document might contain only images or be scanned.');
      }

      setUploadedFiles(prev => prev.map(f => 
        f.file.name === file.name 
          ? { ...f, extractedText, progress: 50, progressMessage: 'Analyzing document content...' }
          : f
      ));

      // Generate study content using AI with enhanced processing
      console.log('Generating comprehensive study content with AI...');
      const studyContent = await generateStudyContent(extractedText);
      
      setUploadedFiles(prev => prev.map(f => 
        f.file.name === file.name 
          ? { ...f, studyContent, progress: 90, progressMessage: 'Finalizing...' }
          : f
      ));

      // Update user profile with localStorage data
      if (userProfile) {
        const currentStats = userProfile.studyStats || {
          documentsUploaded: 0,
          flashcardsCreated: 0,
          quizzesCompleted: 0,
          studyHours: 0
        };

        await updateUserProfile({
          studyStats: {
            ...currentStats,
            documentsUploaded: currentStats.documentsUploaded + 1,
            flashcardsCreated: currentStats.flashcardsCreated + (studyContent.flashcards?.length || 0)
          }
        });
      }

      setUploadedFiles(prev => prev.map(f => 
        f.file.name === file.name 
          ? { ...f, status: 'completed', progress: 100, progressMessage: 'Processing completed successfully!' }
          : f
      ));

      // Call the callback with the generated content
      onUploadComplete({
        fileName: file.name,
        extractedText,
        studyContent,
        fileSize: file.size,
        processedAt: new Date()
      });

      // Store document metadata in localStorage
      const docMeta = {
        fileName: file.name,
        fileSize: file.size,
        processedAt: new Date().toISOString(),
        type: file.type,
        studyContent,
      };
      const existingDocs = JSON.parse(localStorage.getItem('ecstatic_uploaded_documents') || '[]');
      existingDocs.unshift(docMeta); // Add to start for recent first
      localStorage.setItem('ecstatic_uploaded_documents', JSON.stringify(existingDocs));

      console.log('File processing completed successfully:', file.name);

    } catch (error) {
      console.error('Error processing file:', error);
      
      let errorMessage = 'Unknown error occurred';
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Add helpful debug information
        console.error('=== PDF Processing Error Debug Info ===');
        console.error('File name:', file.name);
        console.error('File size:', formatFileSize(file.size));
        console.error('File type:', file.type);
        console.error('Error message:', error.message);
        console.error('PDF.js processing mode:', (window as any).pdfjsWorker || 'not set');
        console.error('Worker source type:', (window as any).pdfjsWorkerSrc || 'not available');
        console.error('To enable detailed debugging, run: window.enableDocumentDebug(true)');
        console.error('To test document processing, run: window.testDocumentProcessor()');
        console.error('=======================================');
        
        // Make debug function available globally for easy access
        (window as any).enableDocumentDebug = (enable: boolean) => {
          console.log('Document debugging', enable ? 'enabled' : 'disabled');
        };
      }

      setUploadedFiles(prev => prev.map(f => 
        f.file.name === file.name 
          ? { ...f, status: 'error', error: errorMessage, progress: 0, progressMessage: 'Processing failed' }
          : f
      ));
    }
  };

  const retryFile = (file: File) => {
    setUploadedFiles(prev => prev.map(f => 
      f.file.name === file.name 
        ? { ...f, status: 'uploading', progress: 0, error: undefined, progressMessage: 'Retrying...' }
        : f
    ));
    processFile(file);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    console.log('Files dropped:', acceptedFiles.length);

    const validFiles: File[] = [];
    const errors: string[] = [];

    acceptedFiles.forEach(file => {
      if (!validateFileType(file)) {
        errors.push(`${file.name}: Unsupported file type. Only PDF and PowerPoint files are allowed.`);
        return;
      }
      if (!validateFileSize(file, 50)) {
        errors.push(`${file.name}: File too large. Maximum size is 50MB.`);
        return;
      }
      if (file.size === 0) {
        errors.push(`${file.name}: File is empty.`);
        return;
      }
      validFiles.push(file);
    });

    // Show errors if any
    if (errors.length > 0) {
      console.error('File validation errors:', errors);
      // You could show these errors in a toast or alert
    }

    if (validFiles.length === 0) {
      return;
    }

    const newFiles: UploadedFile[] = validFiles.map(file => ({
      file,
      status: 'uploading',
      progress: 0,
      progressMessage: 'Preparing file...'
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Process each file
    validFiles.forEach(file => {
      processFile(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: true
  });

  const removeFile = (fileName: string) => {
    setUploadedFiles(prev => prev.filter(f => f.file.name !== fileName));
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return <div className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-success-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-error-600" />;
      default:
        return null;
    }
  };

  const getStatusText = (file: UploadedFile) => {
    switch (file.status) {
      case 'uploading':
        return 'Preparing file...';
      case 'processing':
        return file.progressMessage || `Processing... ${file.progress}%`;
      case 'completed':
        return 'Completed successfully!';
      case 'error':
        return file.error || 'Processing failed';
      default:
        return 'Unknown status';
    }
  };

  return (
    <div className="space-y-6">
      {/* Processing Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Processing Settings
          </h3>
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="flex items-center space-x-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <Settings className="h-4 w-4" />
            <span>{showConfig ? 'Hide' : 'Configure'}</span>
          </button>
        </div>
        
        {showConfig && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={processingConfig.processAllPages}
                    onChange={(e) => setProcessingConfig(prev => ({
                      ...prev,
                      processAllPages: e.target.checked
                    }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Process all pages
                  </span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Extract text from entire document (recommended)
                </p>
              </div>
              
              {!processingConfig.processAllPages && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                    Max pages to process
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={processingConfig.maxPages || 20}
                    onChange={(e) => setProcessingConfig(prev => ({
                      ...prev,
                      maxPages: parseInt(e.target.value) || 20
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <span>Processing mode:</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                processingConfig.processAllPages 
                  ? 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-400'
                  : 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-400'
              }`}>
                {processingConfig.processAllPages ? 'Full Document' : 'Limited Pages'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
          isDragActive
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="p-3 bg-primary-100 dark:bg-primary-900/50 rounded-full">
            <Upload className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {isDragActive ? 'Drop your files here' : 'Upload your documents'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Drag and drop PDF or PowerPoint files, or click to browse
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Maximum file size: 50MB • Supported formats: PDF, PPT, PPTX
            </p>
          </div>
        </div>
      </div>

      {/* Uploaded Files */}
      <AnimatePresence>
        {uploadedFiles.map((uploadedFile) => (
          <motion.div
            key={uploadedFile.file.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                <FileText className="h-8 w-8 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {uploadedFile.file.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(uploadedFile.file.size)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(uploadedFile.status)}
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {getStatusText(uploadedFile)}
                  </span>
                </div>
                
                {uploadedFile.status === 'error' && (
                  <button
                    onClick={() => retryFile(uploadedFile.file)}
                    className="p-1 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                    title="Retry"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                )}
                
                <button
                  onClick={() => removeFile(uploadedFile.file.name)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
                  title="Remove"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {/* Progress Bar */}
            {(uploadedFile.status === 'uploading' || uploadedFile.status === 'processing') && (
              <div className="mt-3">
                <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <motion.div
                    className="bg-primary-600 h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadedFile.progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {uploadedFile.progressMessage || `${uploadedFile.progress}% complete`}
                </p>
              </div>
            )}
            
            {/* Error Details */}
            {uploadedFile.status === 'error' && uploadedFile.error && (
              <div className="mt-3 p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-md">
                <p className="text-sm text-error-700 dark:text-error-300">
                  {uploadedFile.error}
                </p>
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};